import React from "react";
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { COLORS } from "../../../assets/colors";
import { NavLink } from "react-router-dom";
import { ROUTES_PATH } from "../../../utils/Consts";
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

const CategoryCard = styled(Box)(({ theme }) => ({
    background: COLORS.primary,
    borderRadius: '20px',
    padding: '24px',
    marginBottom: '40px',
    boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.3)',
}));

const ProductCard = styled(Paper)(({ theme }) => ({
    background: `linear-gradient(135deg, ${COLORS.primary} 0%, rgba(30, 58, 95, 0.95) 100%)`,
    borderRadius: '16px',
    border: '3px solid #d7b547',
    padding: '16px',
    textAlign: 'center',
    cursor: 'pointer',
    position: 'relative',
    overflow: 'hidden',
    transition: 'all 0.3s ease',
    height: '100%',
    aspectRatio: '0.75',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0px 6px 20px rgba(0, 0, 0, 0.4)',
    [theme.breakpoints.down('sm')]: {
        padding: '12px',
        borderRadius: '12px',
        border: '2px solid #d7b547',
    },
    '&:hover': {
        transform: 'translateY(-8px)',
        boxShadow: '0px 12px 32px rgba(215, 181, 71, 0.4)',
        border: '3px solid #e6c663',
    },
}));




function HomeTopCategoriesUI({ data }) {
    const isMobile = useMediaQuery('(max-width:600px)');

    return (
        <Box sx={{
            padding: { xs: '40px 20px', sm: '40px 20px', md: '60px 40px', lg: '60px 80px', xl: '60px 120px' },
            background: '#f5f5f5'
        }}>
            {/* Título Principal */}
            <Box sx={{ textAlign: 'center', marginBottom: '40px' }}>
                <Typography 
                    variant="h2" 
                    component="h2"
                    sx={{ 
                        color: '#d7b547',
                        fontFamily: 'Quicksand',
                        fontWeight: 'bold',
                        fontSize: { xs: '2.2rem', sm: '2.8rem', md: '3.5rem' },
                        marginBottom: '20px',
                        textShadow: '0 0 10px rgba(215, 181, 71, 0.4)'
                    }}>
                    Top Categorías
                </Typography>
                <Typography 
                    variant="body1" 
                    component="p"
                    sx={{ 
                        color: '#d7b547',
                        fontFamily: 'Quicksand',
                        fontSize: { xs: '1rem', sm: '1.1rem', md: '1.2rem' },
                        maxWidth: '900px',
                        margin: '0 auto',
                        lineHeight: 1.6
                    }}>
                    Descubre los productos más populares y solicitados de nuestra panadería, creados especialmente para ti
                </Typography>
            </Box>

            {/* Categorías */}
            {data.map(category => (
                <CategoryCard key={category.id}>
                    {/* Header con nombre de categoría y botón Ver Todo */}
                    <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        marginBottom: '32px',
                        flexWrap: 'wrap',
                        gap: '16px'
                    }}>
                        <Typography 
                            variant="h3" 
                            component="h3"
                            sx={{
                                fontFamily: 'Quicksand',
                                fontSize: { xs: '1.8rem', sm: '2.2rem', md: '2.5rem' },
                                fontWeight: 'bold',
                                color: '#d7b547',
                                textShadow: '0 0 8px rgba(215, 181, 71, 0.5)'
                            }}>
                            {category.name} :
                        </Typography>
                        
                        <NavLink
                            to={`${ROUTES_PATH.CATEGORIES}/${category.id}`}
                            style={{ textDecoration: 'none' }}>
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                color: '#d7b547',
                                cursor: 'pointer',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    color: '#e6c663',
                                    transform: 'translateX(4px)',
                                }
                            }}>
                                <Typography sx={{
                                    fontFamily: 'Quicksand',
                                    fontSize: { xs: '1rem', sm: '1.1rem' },
                                    fontWeight: '600',
                                    textTransform: 'uppercase',
                                    letterSpacing: '1px'
                                }}>
                                    VER TODO
                                </Typography>
                                <ArrowForwardIcon />
                            </Box>
                        </NavLink>
                    </Box>

                    {/* Subcategorías */}
                    <Box sx={{ 
                        display: 'flex', 
                        gap: '16px', 
                        flexWrap: 'wrap',
                        marginBottom: '32px'
                    }}>
                        {category.subCategories.slice(0, 6).map((subcategory, index) => (
                            <Box key={`${category.id}-${subcategory.id}-${index}`}>
                                <NavLink
                                    to={{
                                        pathname: `${ROUTES_PATH.CATEGORIES}/${category.id}`,
                                        search: `?subCategoryId=${subcategory.id}&subCategoryName=${encodeURIComponent(subcategory.name)}`,
                                    }}
                                    style={{ textDecoration: 'none' }}>
                                    <Typography sx={{
                                        fontFamily: 'Quicksand',
                                        fontSize: { xs: '0.9rem', sm: '1rem' },
                                        color: '#d7b547',
                                        cursor: 'pointer',
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                            color: '#e6c663',
                                            textDecoration: 'underline',
                                        }
                                    }}>
                                        {subcategory.name}
                                    </Typography>
                                </NavLink>
                            </Box>
                        ))}
                    </Box>

                    {/* Grid de Productos */}
                    <Grid container spacing={2}>
                        {category.products.slice(0, isMobile ? 4 : 5).map(product => (
                            <Grid item xs={6} sm={6} md={4} lg={2.4} key={product.id}>
                                <NavLink
                                    to={{
                                        pathname: `${ROUTES_PATH.CATEGORIES}/${product.categoryId}`,
                                        search: `?subCategoryId=${product.subCategoryId}&subCategoryName=${encodeURIComponent(product.subcategoryName)}`,
                                    }}
                                    style={{ textDecoration: 'none' }}>
                                    <ProductCard elevation={0}>
                                        {/* Código del producto */}
                                        <Typography sx={{
                                            fontFamily: 'Quicksand',
                                            fontSize: { xs: '0.85rem', sm: '1.2rem' },
                                            fontWeight: 'bold',
                                            color: '#ffffff',
                                            marginBottom: { xs: '8px', sm: '16px' },
                                            textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
                                        }}>
                                            {product.name}
                                        </Typography>
                                        
                                        {/* Imagen del producto */}
                                        <Box sx={{
                                            width: '100%',
                                            flex: 1,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            background: 'rgba(255, 255, 255, 0.1)',
                                            borderRadius: { xs: '8px', sm: '12px' },
                                            padding: { xs: '6px', sm: '8px' },
                                            overflow: 'hidden'
                                        }}>
                                            <img 
                                                src={product.image} 
                                                alt={product.name}
                                                loading="lazy"
                                                style={{ 
                                                    width: '100%',
                                                    height: '100%',
                                                    objectFit: 'contain',
                                                    borderRadius: '8px'
                                                }} 
                                            />
                                        </Box>
                                    </ProductCard>
                                </NavLink>
                            </Grid>
                        ))}
                    </Grid>
                </CategoryCard>
            ))}
        </Box>
    );
}

export { HomeTopCategoriesUI }; 